import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Baggage } from '../../../../../BaggageService/types';
import { Money } from '../../../../../Money';
import { useTheme } from '../../../../../theme';
import BaggageComponent from '../../../../Baggage';
import { serviceForMultipleSegments } from '../../../hooks';
import { BaggageType } from '@websky/graphql';
import { getRelatedSegments, getActualPriceInBaggageGroup } from '../../../utils';
import { Money as MoneyType } from '../../../../../types';

interface Props {
	services: Baggage[];
	allSegments?: boolean;
}

const HeaderBaggageItem: React.FC<Props> = ({ services, allSegments }) => {
	const theme = useTheme('UpgradeBaggageDialog').BaggageDialogHeader;
	const { t } = useTranslation('IncludedBaggage');

	const multipleSegments = React.useMemo(() => serviceForMultipleSegments(services[0]), [services]);

	const price: MoneyType & { withFrom?: boolean } = React.useMemo(() => {
		if (allSegments || multipleSegments) {
			const pricesForPassengers = new Map<string, number>();

			services.forEach(service => {
				if (service.canBeAdded) {
					const segments = getRelatedSegments(service.segmentIds);

					service.allowedPassengers.forEach(passengerId => {
						pricesForPassengers.set(
							passengerId,
							(pricesForPassengers.get(passengerId) || 0) + service.price.amount * segments.size
						);
					});
				}
			});

			const prices = Array.from(pricesForPassengers.values()),
				minPrice = prices?.length ? Math.min(...prices) : 0;

			return {
				currency: services[0]?.price.currency,
				amount: minPrice,
				withFrom: prices.some(price => price > minPrice)
			};
		}

		return getActualPriceInBaggageGroup(services, multipleSegments);
	}, [services, allSegments, multipleSegments]);

	const isShort = [BaggageType.BaggageExcess].includes(services[0].type);

	return (
		<div className={theme.baggage_wrapper} key={services[0].id} data-rfisc={services[0].rfics}>
			<BaggageComponent
				baggage={services[0]}
				selected={false}
				realIconSize={true}
				onlyIcon={false}
				short={isShort}
			/>

			<div className={theme.price_wrapper}>
				{price.withFrom && t('from')}

				<Money
					money={{
						currency: price.currency,
						amount: price.amount
					}}
				/>
			</div>

			{!allSegments && multipleSegments && (
				<div className={theme.segmentNotice}>{t('Added for all flights')}</div>
			)}
		</div>
	);
};

export default HeaderBaggageItem;
